import { useState } from 'react'

interface BaseFilters {
  states: string[]
  carrier_names: string[]
  renewed_employers_only: boolean
}

interface UsePlanTableFiltersOptions<T extends BaseFilters> {
  initialFilters: T
  onFiltersChange?: () => void
}

interface UsePlanTableFiltersReturn<T extends BaseFilters> {
  filters: T
  updateMultiSelectFilter: (filterKey: 'states' | 'carrier_names', values: string[]) => void
  updateSingleSelectFilter: <K extends keyof T>(filterKey: K, value: T[K] extends string ? string : never) => void
  toggleBooleanFilter: <K extends keyof T>(filterKey: K) => void
  handleMultiSelectToggle: (filterType: 'states' | 'carrier_names', value: string) => void
  handleSingleSelectToggle: <K extends keyof T>(filterType: K, value: string) => void
  clearFilters: () => void
  getActiveFilterCount: () => number
}

export function usePlanTableFilters<T extends BaseFilters> (
  options: UsePlanTableFiltersOptions<T>
): UsePlanTableFiltersReturn<T> {
  const { initialFilters, onFiltersChange } = options
  const [filters, setFilters] = useState<T>(initialFilters)

  const updateFiltersAndNotify = (updater: (prev: T) => T) => {
    setFilters(updater)
    onFiltersChange?.()
  }

  // Update multi-select filters (states, carrier_names)
  const updateMultiSelectFilter = (filterKey: 'states' | 'carrier_names', values: string[]) => {
    updateFiltersAndNotify(prev => ({ ...prev, [filterKey]: values }))
  }

  // Update single-select filters (like sources)
  const updateSingleSelectFilter = <K extends keyof T>(
    filterKey: K,
    value: T[K] extends string ? string : never
  ) => {
    updateFiltersAndNotify(prev => ({ ...prev, [filterKey]: value }))
  }

  // Toggle boolean filters (like show_deactivated, renewed_employers_only)
  const toggleBooleanFilter = <K extends keyof T>(filterKey: K) => {
    updateFiltersAndNotify(prev => ({
      ...prev,
      [filterKey]: !prev[filterKey],
    }))
  }

  // Handle toggling multi-select values
  const handleMultiSelectToggle = (filterType: 'states' | 'carrier_names', value: string) => {
    const currentValues = filters[filterType]
    const newValues = currentValues.includes(value)
      ? currentValues.filter(item => item !== value)
      : [...currentValues, value]

    updateMultiSelectFilter(filterType, newValues)
  }

  // Handle toggling single-select values
  const handleSingleSelectToggle = <K extends keyof T>(filterType: K, value: string) => {
    const currentValue = filters[filterType]
    const newValue = (currentValue === value ? '' : value) as T[K] extends string ? string : never
    updateSingleSelectFilter(filterType, newValue)
  }

  // Clear all filters
  const clearFilters = () => {
    updateFiltersAndNotify(() => initialFilters)
  }

  // Get active filter count for "Clear all" button
  const getActiveFilterCount = () => {
    let count = 0

    // Count array filters
    if (filters.states.length > 0) count += filters.states.length
    if (filters.carrier_names.length > 0) count += filters.carrier_names.length

    // Count boolean filters
    if (filters.renewed_employers_only) count += 1

    // Count additional filters dynamically
    Object.entries(filters).forEach(([key, value]) => {
      if (key !== 'states' && key !== 'carrier_names' && key !== 'renewed_employers_only') {
        if (typeof value === 'boolean' && value) {
          count += 1
        } else if (typeof value === 'string' && value) {
          count += 1
        }
      }
    })

    return count
  }

  return {
    filters,
    updateMultiSelectFilter,
    updateSingleSelectFilter,
    toggleBooleanFilter,
    handleMultiSelectToggle,
    handleSingleSelectToggle,
    clearFilters,
    getActiveFilterCount,
  }
}
